import API_URL from '../apiconfig';
import axios from './api';
import { getApiErrorMessage } from './apiError';
import {
  GalleryContext,
  GalleryImage,
  GalleryUploadSignature,
} from '../types/gallery';

function galleryPath(context: GalleryContext) {
  return context.kind === 'booking-site'
    ? `${API_URL}/v1/booking-site/${context.businessId}/images`
    : `${API_URL}/v1/employees/${context.employeeId}/images`;
}

export async function fetchGallery(
  context: GalleryContext,
): Promise<GalleryImage[]> {
  try {
    const response = await axios.get(galleryPath(context));
    return response?.data?.result ?? [];
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Cannot get gallery photos'), {
      cause: error,
    });
  }
}

export async function commitGallery(
  context: GalleryContext,
  imageRefs: string[],
): Promise<GalleryImage[]> {
  try {
    const response = await axios.put(galleryPath(context), { imageRefs });
    return response?.data?.result ?? [];
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Cannot save gallery photos'), {
      cause: error,
    });
  }
}

export async function getGalleryUploadSignature(
  context: GalleryContext,
): Promise<GalleryUploadSignature> {
  try {
    const response = await axios.post(
      `${galleryPath(context)}/upload-signature`,
    );
    return response.data.result;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Could not upload that photo.'), {
      cause: error,
    });
  }
}

export function uploadToCloudinary(
  file: Blob,
  fileName: string,
  signed: GalleryUploadSignature,
  onProgress: (percent: number) => void,
): Promise<GalleryImage> {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file, fileName);
    formData.append('api_key', signed.apiKey);
    formData.append('timestamp', String(signed.timestamp));
    formData.append('signature', signed.signature);
    formData.append('folder', signed.folder);
    // Signed by the API, so these have to go up verbatim.
    formData.append('allowed_formats', signed.allowedFormats);

    const request = new XMLHttpRequest();
    request.open(
      'POST',
      `https://api.cloudinary.com/v1_1/${signed.cloudName}/image/upload`,
    );

    request.upload.onprogress = event => {
      if (!event.lengthComputable) return;
      onProgress(Math.round((event.loaded / event.total) * 100));
    };

    request.onload = () => {
      let body: { public_id?: string; error?: { message?: string } } = {};
      try {
        body = JSON.parse(request.responseText);
      } catch {
        body = {};
      }
      if (request.status < 200 || request.status >= 300 || !body.public_id) {
        reject(
          new Error(body.error?.message || 'Could not upload that photo.'),
        );
        return;
      }
      resolve({ imageRef: body.public_id });
    };
    request.onerror = () => reject(new Error('Could not upload that photo.'));

    request.send(formData);
  });
}
